import {a11yStandardKeys} from "../constants/constants";
import {iconsPriority, PriorityType} from "../constants/icons";
import {Accessibility} from "../models/Accessibility";
import {AccessibilityUnit} from "../models/AccessibilityUnits";
import {MergedAccessibilityAttr} from "../models/MergedAccessibilityAttr";
import {getTextByCurrentLanguage} from "./language";

function getImageOrFallback(url: string) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => resolve(url);
    img.onerror = (ev) => {
      reject(ev);
    };
  });
}
async function asyncFilter<T>(
  arr: T[],
  predicate: (obj: T, index?: number, array?: T[]) => {}
) {
  const results = await Promise.all(arr.map(predicate));

  return arr.filter((_v, index) => results[index]);
}

const filterByGroupAttribute = (accessibilities: MergedAccessibilityAttr[], priorities: PriorityType) => {
  let copyAccessibilities = accessibilities
  const groupedAccessibilities: {
    accessibility: MergedAccessibilityAttr,
    GROUP: number,
    PRIORITY: number
  }[] = []
  copyAccessibilities.forEach((i) => {
    const priority = priorities[i.attribute]
    if (priority && priority.FILTER_GROUP) {
      groupedAccessibilities.push({
        ...priority.FILTER_GROUP,
        accessibility: i,
      })
    }
  })

  if (groupedAccessibilities.length > 0) {
    // Filter out elements based on priority and group
    copyAccessibilities = copyAccessibilities.filter((i) => {
      const findInGrouped = groupedAccessibilities
        .find((item) => item.accessibility.id === i.id);
      if (findInGrouped) {
        const filterByGroup = groupedAccessibilities
          .filter((item) => findInGrouped.GROUP === item.GROUP);
        // Check if any element in the same group has higher priority
        const hasHigherPriority = filterByGroup.some((item) => (
          item.PRIORITY > findInGrouped.PRIORITY
        ));
        // Check if any element in the same group has equal priority but different id
        const hasEqualPriorityDifferentId = filterByGroup.some((item) => (
          item.PRIORITY === findInGrouped.PRIORITY && item.accessibility.id !== findInGrouped.accessibility.id
        ));
        // Return false if there's a higher priority
        // or there's equal priority but different id
        return !hasHigherPriority || hasEqualPriorityDifferentId;
      }
      return true;
    });
  }


  return copyAccessibilities
}

const sortByGroupAttribute = (accessibilities: MergedAccessibilityAttr[], priorities: PriorityType) => {
  let copyAccessibilities = [...accessibilities];

  const groupedAccessibilities: {
    accessibility: MergedAccessibilityAttr,
    GROUP: number,
    PRIORITY: number
  }[] = [];

  copyAccessibilities.forEach((i) => {
    const priority = priorities[i.attribute];
    if (priority && priority.SORT_GROUP) {
      groupedAccessibilities.push({
        ...priority.SORT_GROUP,
        accessibility: i,
      });
    }
  });

  if (groupedAccessibilities.length > 0) {
    copyAccessibilities = copyAccessibilities.sort((a, b) => {
      const groupA = groupedAccessibilities.find(item => item.accessibility.id === a.id);
      const groupB = groupedAccessibilities.find(item => item.accessibility.id === b.id);
      if (!groupA || !groupB || groupA.GROUP !== groupB.GROUP) {
        return 0;
      }
      return groupA.PRIORITY - groupB.PRIORITY;
    });
  }
  return copyAccessibilities;
}

const getPriorityAttribute = (
  attribute: string,
  disabilityTypes: string[],
  iconsPriority: any,
  isBuildingArea: boolean
) => {
  let filterTypes = ['G']

  if (disabilityTypes?.length > 0) {
    filterTypes = disabilityTypes
  }

  if (isBuildingArea) {
    filterTypes = ['BA']
  }

  if (attribute) {
    const priorities = iconsPriority[attribute]
    if (priorities && filterTypes.length > 1) {
      // From this it turns out that if we use multiple disabilities, then we will always give priority to the
      // icons that are more important for the selected disabilities
      const sortedArr = Object.entries(priorities).sort(
        (a, b) => (a[1] as number) - (b[1] as number)
      );
      const filterArrByDisabilityTypes = sortedArr.filter((item) => (
        filterTypes.includes(item[0])
      ))
      const filterOutNull = filterArrByDisabilityTypes?.filter((item) => item[1] !== null)

      if (filterOutNull?.length > 0) {
        return filterOutNull[0][1]
      }
      return null
    } else {
      return priorities[filterTypes[0]]
    }
  }
  return null
}

export const getIcons = async (
  accessibilities: Accessibility[],
  a11yStandardOrUnitType: string,
  accessibilityUnit: AccessibilityUnit[],
  disabilityTypes: string[],
  isBuildingArea: boolean,
): Promise<MergedAccessibilityAttr[]> => {

  // [plug for removing duplicate icons]
  // if we are using for example type_of_fall_protection_base__artificial_grass icon
  // then we will get duplicate with surface_type_playground__artificial_grass
  const attributeReplacement: { [key: string]: string } = {
    'type_of_fall_protection_base__artificial_grass': 'surface_type_playground__artificial_grass',
    'type_of_fall_protection_base__asphalt': 'surface_type_playground__asphalt',
    'type_of_fall_protection_base__grass': 'surface_type_playground__grass',
    'type_of_fall_protection_base__loose_gravel': 'surface_type_playground__loose_gravel',
    'type_of_fall_protection_base__stone_flour': 'surface_type_playground__stone_flour',
  }

  const accessibilitiesReplacement = accessibilities.map((i) => {
    if (Object.keys(attributeReplacement).includes(i.attribute)) {
      i.attribute = attributeReplacement[i.attribute]
    }
    return i
  })

  const iconsPriorityByStandard = iconsPriority[a11yStandardOrUnitType] ?
      iconsPriority[a11yStandardOrUnitType] :
      iconsPriority[a11yStandardKeys.hotel]

  const filterByStandard = accessibilitiesReplacement.filter((acc) => (
    Object.keys(iconsPriorityByStandard).includes(acc.attribute)
  ))

  const sortByPriority = filterByStandard.sort((a, b) => (
    getPriorityAttribute(a.attribute, disabilityTypes, iconsPriorityByStandard, isBuildingArea) -
    getPriorityAttribute(b.attribute, disabilityTypes, iconsPriorityByStandard, isBuildingArea)
  ))

  const filterOutNull = sortByPriority.filter((acc) => (
    getPriorityAttribute(acc.attribute, disabilityTypes, iconsPriorityByStandard, isBuildingArea) !== null
  ))

  const filtered = await asyncFilter(filterOutNull, async (acc) => {
    try {
      await getImageOrFallback(acc.attribute_icon);
      return true;
    } catch (e) {
      console.log("image fallback in catch");
      return false;
    }
  });

  const mergedArray: MergedAccessibilityAttr[] = []

  filtered.forEach((acc) => {
    const unit = accessibilityUnit.find((unit) =>
      unit.id === acc.accessibility_unit);
    if (unit) {
      mergedArray.push({
        id: acc.id,
        item: acc.item,
        attribute: acc.attribute,
        item_title: acc.item_title,
        attribute_title: acc.attribute_title,
        attribute_icon: acc.attribute_icon,
        accessibility_unit: acc.accessibility_unit,
        is_absent: acc.is_absent,
        unit_names: getTextByCurrentLanguage(unit.name),
        unit_titles: unit.title
      })
    }
  })

  mergedArray.filter((access) => access && !access.is_absent);

  let withoutDuplicate = mergedArray.filter((obj, index, arr) =>
    index === arr.findIndex((x) => (
      (x && obj) && (x.attribute === obj.attribute)
    ))
  )
  const filteredByGroupAttribute = filterByGroupAttribute(withoutDuplicate, iconsPriorityByStandard)
  const sortedByGroupAttribute = sortByGroupAttribute(filteredByGroupAttribute, iconsPriorityByStandard)

  const addedUnitIds = sortedByGroupAttribute.map((i) => {
    return {
      ...i,
      unit_ids: accessibilities.filter(x =>
        (x && i) && i.attribute === x.attribute).map((i) => i && i.accessibility_unit),
      disability_types: [...new Set(accessibilities.filter(x =>
        (x && i) && i.attribute === x.attribute).flatMap((i) => i && i.disability_types))]
    }
  })

  return addedUnitIds.map(obj => {
    const filteredArr = mergedArray.filter(x =>
      (x && obj) && obj.attribute === x.attribute);
    return {
      ...obj,
      unit_names: filteredArr.map(y =>
        (y) && y.unit_names).toString().replaceAll(",", ", "),
      unit_titles: filteredArr.map(y =>
        (y) && y.unit_titles).toString().replaceAll(",", ", "),
    }
  });
};
